import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons/Icon';
import { AvatarSourceContext, type AvatarSource } from './Avatar';
import { Button, IconButton } from './controls';
import { ErrorBoundary } from './ErrorBoundary';
import { isComposing } from './keys';
import { TooltipHost } from './tooltip';

// Escape stack: the most recently opened layer (popover → modal → panel) closes first.

const escapeStack: Array<{ id: number; handler: () => void }> = [];
let escapeIds = 0;
let escapeListening = false;
const escapeWatchers = new Set<() => void>();
const escapeChanged = () => escapeWatchers.forEach((notify) => notify());

/** Closes the top layer as Escape would (a phone's back button does this). False when none is open. */
export function closeTopLayer(): boolean {
  const top = escapeStack[escapeStack.length - 1];
  top?.handler();
  return Boolean(top);
}

/** How many layers that Escape closes are open (popovers, dialogs, the floating info panel). */
export function useOpenLayers(): number {
  return useSyncExternalStore(
    (notify) => {
      escapeWatchers.add(notify);
      return () => escapeWatchers.delete(notify);
    },
    () => escapeStack.length,
    () => 0,
  );
}

function ensureEscapeListener() {
  if (escapeListening || typeof window === 'undefined') return;
  escapeListening = true;
  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || isComposing(event)) return;
    const top = escapeStack[escapeStack.length - 1];
    if (!top) return;
    event.preventDefault();
    top.handler();
  });
}

/** Registers `handler` for Escape while `active`. Later registrations win. */
export function useEscape(handler: () => void, active = true) {
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    if (!active) return undefined;
    ensureEscapeListener();
    const id = ++escapeIds;
    escapeStack.push({ id, handler: () => ref.current() });
    escapeChanged();
    return () => {
      const index = escapeStack.findIndex((entry) => entry.id === id);
      if (index >= 0) escapeStack.splice(index, 1);
      escapeChanged();
    };
  }, [active]);
}

// Focus: what Tab reaches inside dialogs and popovers

const TABBABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]';

/** Elements Tab stops at inside `root`, in document order (one per radio group, like the browser). */
function tabbables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(TABBABLE)].filter((el) => {
    if (el.tabIndex < 0 || el.checkVisibility?.({ visibilityProperty: true }) === false) return false;
    if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || el.checked || !el.name) return true;
    return !root.querySelector(`input[type="radio"][name="${CSS.escape(el.name)}"]:checked`);
  });
}

/** The control that opened a dialog or popover, and the focusable container it sits in (its message), if any. */
interface FocusReturn {
  opener: HTMLElement;
  container: HTMLElement | null;
}

function focusReturnTo(opener: Element | null): FocusReturn | null {
  if (!(opener instanceof HTMLElement) || opener === document.body) return null;
  return { opener, container: opener.parentElement?.closest<HTMLElement>('[tabindex]') ?? null };
}

/**
 * Gives focus back to the opener or, when it cannot take it (shown only while its message holds focus, or gone
 * with the message), to its container. Returns whether one of them took it.
 */
function giveFocusBack(back: FocusReturn | null, options?: FocusOptions): boolean {
  if (!back) return false;
  const { opener, container } = back;
  if (opener.isConnected) opener.focus(options);
  if (document.activeElement !== opener && container?.isConnected) container.focus(options);
  return document.activeElement === opener || (container !== null && document.activeElement === container);
}

/**
 * Dialog focus (WAI-ARIA dialog pattern): focus moves in on open, Tab and
 * Shift+Tab wrap inside, and closing returns focus to the control that opened
 * it (returned here) unless the action already moved focus elsewhere. The
 * cleanup runs while the dialog is still in the DOM, so focus never falls to
 * the page body in between.
 *
 * The page behind stays in the accessibility tree (`aria-modal` marks it):
 * making it `inert` took the whole app out of the tree and back on close, after
 * which Orca had lost the window and stopped announcing focus.
 */
function useModalFocus(ref: RefObject<HTMLDivElement | null>): RefObject<FocusReturn | null> {
  const back = useRef<FocusReturn | null>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    back.current = focusReturnTo(document.activeElement);
    const timer = window.setTimeout(() => {
      (root.querySelector<HTMLElement>('[data-autofocus]') ?? tabbables(root)[0] ?? root.querySelector<HTMLElement>('.dialog'))?.focus();
    }, 30);
    return () => {
      window.clearTimeout(timer);
      const active = document.activeElement;
      if (!active || active === document.body || root.contains(active)) giveFocusBack(back.current);
    };
  }, [ref]);
  return back;
}

function wrapTab(event: ReactKeyboardEvent<HTMLElement>) {
  if (event.key !== 'Tab' || event.defaultPrevented) return;
  const items = tabbables(event.currentTarget);
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !event.currentTarget.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !event.currentTarget.contains(active))) {
    event.preventDefault();
    first.focus();
  }
}

// Modal (`.modal` + `.dialog.glass`)

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name source (id of the heading inside). */
  labelledBy: string;
  /** Design ids such as `m-settings` carry special placement rules. */
  id?: string;
  size?: 'wide' | 'small' | 'side';
  role?: 'dialog' | 'alertdialog';
  className?: string;
  dialogClassName?: string;
  children: ReactNode;
}

export function Modal({ open, ...props }: ModalProps) {
  // Closing unmounts the layer, so its focus cleanup runs before its DOM is removed.
  return open ? <ModalLayer {...props} /> : null;
}

/** Open dialogs that are not closing yet, the top one last. */
const dialogs: Array<(close: () => void) => void> = [];

/**
 * For a control inside a dialog that only closes it (the ✕, 닫기, 취소): dismisses the top dialog the way
 * Escape does, then runs `close` (what its onClose does). With no dialog open, runs `close` at once.
 */
export function dismissDialog(close: () => void) {
  const top = dialogs[dialogs.length - 1];
  if (top) top(close);
  else close();
}

/** The fade-out of a dismissed dialog (`.modal.out`): it leaves the DOM when this ends. */
const DISMISS_MS = 200;

function ModalLayer({ onClose, labelledBy, id, size, role = 'dialog', className, dialogClassName, children }: Omit<ModalProps, 'open'>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const back = useModalFocus(rootRef);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(0);
  // Focus goes back first and the dialog leaves after its fade-out. Chromium reports a removal before a focus
  // change made about the same time, and Orca then takes the new focus without announcing it (after Escape from
  // a text field even 50 ms apart, and after the ✕ in one run of two).
  const dismiss = useCallback(
    (close: () => void) => {
      if (closeTimer.current) return;
      if (!giveFocusBack(back.current)) {
        close();
        return;
      }
      setClosing(true);
      closeTimer.current = window.setTimeout(close, DISMISS_MS);
    },
    [back],
  );
  useEffect(() => {
    if (closing) return undefined;
    dialogs.push(dismiss);
    return () => {
      const index = dialogs.indexOf(dismiss);
      if (index >= 0) dialogs.splice(index, 1);
    };
  }, [dismiss, closing]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  useEscape(() => dismiss(onClose), !closing);

  return createPortal(
    <div
      ref={rootRef}
      id={id}
      className={['modal', 'show', closing && 'out', className].filter(Boolean).join(' ')}
      role={role}
      aria-modal="true"
      aria-labelledby={labelledBy}
      onKeyDown={wrapTab}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) dismiss(onClose);
      }}
    >
      <div className={['dialog', 'glass', size, dialogClassName].filter(Boolean).join(' ')} tabIndex={-1}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function DialogHead({ titleId, title, onClose }: { titleId: string; title: ReactNode; onClose?: () => void }) {
  return (
    <div className="dlg-head">
      <h2 id={titleId}>{title}</h2>
      {onClose ? <IconButton icon="x" label="닫기" onClick={() => dismissDialog(onClose)} /> : null}
    </div>
  );
}

// Toasts (`#toasts` + `.toast`)

export interface ToastOptions {
  action?: string;
  onAction?: () => void;
  /** Visible time in ms (default 3600). A toast with an action stays until it is used or closed. */
  ms?: number;
  icon?: Parameters<typeof Icon>[0]['name'];
}

interface ToastItem extends ToastOptions {
  id: number;
  message: ReactNode;
  leaving: boolean;
}

type ToastFn = (message: ReactNode, options?: ToastOptions) => void;
const ToastContext = createContext<ToastFn>(() => {});
export const useToast = () => useContext(ToastContext);

function ToastHost({ items, dismiss }: { items: ToastItem[]; dismiss: (id: number) => void }) {
  return createPortal(
    <div id="toasts" aria-live="polite">
      {items.map((item) => (
        <div key={item.id} className={`toast${item.leaving ? ' out' : ''}`}>
          <span>
            {item.icon ? (
              <>
                <Icon name={item.icon} size="sm" />{' '}
              </>
            ) : null}
            {item.message}
          </span>
          {item.action ? (
            <>
              <button
                type="button"
                onClick={() => {
                  item.onAction?.();
                  dismiss(item.id);
                }}
              >
                {item.action}
              </button>
              <button type="button" className="toast-x" aria-label="알림 닫기" onClick={() => dismiss(item.id)}>
                <Icon name="x" size="xs" />
              </button>
            </>
          ) : null}
        </div>
      ))}
    </div>,
    document.body,
  );
}

// Popover (`.pop`) anchored to an element, like the design's openPop()

export interface PopoverOptions {
  align?: 'left' | 'right';
  role?: 'menu' | 'dialog';
  label?: string;
}

interface PopoverState extends PopoverOptions {
  anchor: HTMLElement;
  render: (close: () => void) => ReactNode;
  /** Where the opener gets profile photos: the host renders at the root, outside the session's provider. */
  avatars: AvatarSource | null;
}

interface PopoverApi {
  open: (anchor: HTMLElement, render: (close: () => void) => ReactNode, options?: PopoverOptions) => void;
  close: () => void;
}
const PopoverContext = createContext<{ open: (state: PopoverState) => void; close: () => void }>({ open: () => {}, close: () => {} });

/**
 * Popover content renders in the host at the root, outside the providers around the control that opens it: it takes
 * the opener's photo source along, so people show with their photos there too. Other values are passed in (the client).
 */
export function usePopover(): PopoverApi {
  const host = useContext(PopoverContext);
  const avatars = useContext(AvatarSourceContext);
  return useMemo(() => ({ open: (anchor, render, options) => host.open({ ...options, anchor, render, avatars }), close: host.close }), [host, avatars]);
}

/**
 * Where a popover goes in the DOM: into the landmark of the control that opened it (the chat's `main`), so it is
 * inside a landmark and a screen reader's reading order meets it right after that control; otherwise the body.
 * That landmark is a glass pane that clips its content, so there the popover is placed in the pane's coordinates
 * and kept inside it (`.pop.in-landmark`).
 */
function popoverHome(anchor: HTMLElement): HTMLElement {
  return anchor.closest<HTMLElement>('main') ?? document.body;
}

function PopoverHost({ state, close }: { state: PopoverState | null; close: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useEscape(close, Boolean(state));
  const home = state ? popoverHome(state.anchor) : null;
  const inLandmark = Boolean(home && home !== document.body);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!state || !el || !home) return undefined;
    // Above the anchor when it fits, otherwise below; re-measured when async content changes the size.
    // An anchor without a box (hidden, or gone with its message) has no place to give: the last one it had is kept.
    let anchorBox: DOMRect | null = null;
    const place = () => {
      if (state.anchor.getClientRects().length) anchorBox = state.anchor.getBoundingClientRect();
      const rect = anchorBox;
      if (!rect) return;
      const box = inLandmark ? home.getBoundingClientRect() : { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
      const pw = el.offsetWidth;
      const ph = el.offsetHeight;
      let x = state.align === 'right' ? rect.right - pw : rect.left;
      let y = rect.top - ph - 8;
      if (y < box.top + 8) y = rect.bottom + 8;
      x = Math.max(box.left + 10, Math.min(x, box.right - pw - 10));
      y = Math.max(box.top + 10, Math.min(y, box.bottom - ph - 10));
      // In the pane, left and top count from its padding box.
      if (inLandmark) {
        x -= box.left + home.clientLeft;
        y -= box.top + home.clientTop;
      }
      setPos((prev) => (prev && prev.left === x && prev.top === y ? prev : { left: x, top: y }));
    };
    place();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [state, home, inLandmark]);

  useEffect(() => {
    if (!state) return undefined;
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || state.anchor.contains(target)) return;
      close();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [state, close]);

  // Like a menu button's popup: focus moves to the first item, and back to the anchor when the popover closes with focus inside it.
  useEffect(() => {
    const el = ref.current;
    if (!state || !el) return undefined;
    const { anchor } = state;
    const expands = anchor.hasAttribute('aria-haspopup');
    const back = focusReturnTo(anchor);
    if (expands) anchor.setAttribute('aria-expanded', 'true');
    (el.querySelector<HTMLElement>('[role="menuitem"]') ?? tabbables(el)[0] ?? el).focus({ preventScroll: true });
    return () => {
      const active = document.activeElement;
      // Still expanded here: an anchor shown only while its popup is open (the message menu button) can take focus.
      if (!active || active === document.body || el.contains(active)) giveFocusBack(back, { preventScroll: true });
      if (expands) anchor.setAttribute('aria-expanded', 'false');
    };
  }, [state]);

  if (!state || !home) return null;
  const role = state.role ?? 'menu';
  return createPortal(
    <div
      ref={ref}
      className={`pop show${inLandmark ? ' in-landmark' : ''}`}
      role={role}
      aria-label={state.label}
      tabIndex={-1}
      style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
      onKeyDown={(event) => {
        if (event.key === 'Tab' && (role === 'menu' || !tabbables(event.currentTarget).length)) {
          // Tab leaves a menu (WAI-ARIA menu button), or a popover with nothing to tab to, from where it was opened.
          state.anchor.focus({ preventScroll: true });
          close();
          return;
        }
        if (role !== 'menu') {
          wrapTab(event);
          return;
        }
        const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]')];
        const index = items.indexOf(document.activeElement as HTMLElement);
        const last = items.length - 1;
        const target =
          event.key === 'ArrowDown'
            ? items[index < last ? index + 1 : 0]
            : event.key === 'ArrowUp'
              ? items[index > 0 ? index - 1 : last]
              : event.key === 'Home'
                ? items[0]
                : event.key === 'End'
                  ? items[last]
                  : undefined;
        if (target) {
          event.preventDefault();
          target.focus();
        }
      }}
    >
      <AvatarSourceContext.Provider value={state.avatars}>
        <ErrorBoundary fallback={() => <h2>표시할 수 없어요</h2>}>{state.render(close)}</ErrorBoundary>
      </AvatarSourceContext.Provider>
    </div>,
    home,
  );
}

// Confirm dialog (design confirmDlg)

export interface ConfirmOptions {
  title: ReactNode;
  body: ReactNode;
  ok?: string;
  danger?: boolean;
}
type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn>(async () => false);
export const useConfirm = () => useContext(ConfirmContext);

function ConfirmHost({ request, settle }: { request: ConfirmOptions | null; settle: (value: boolean) => void }) {
  return (
    <Modal open={Boolean(request)} onClose={() => settle(false)} labelledBy="mc-t" id="m-confirm" role="alertdialog" size="small">
      {request ? (
        <>
          <DialogHead titleId="mc-t" title={request.title} />
          <div className="dlg-body">
            <p style={{ margin: 0, color: 'var(--ink-2)', fontSize: 14.5, lineHeight: 1.6 }}>{request.body}</p>
          </div>
          <div className="dlg-foot">
            <span className="grow" />
            {/* A destructive confirm starts on the safe choice, so Enter or Space alone never deletes. */}
            <Button variant="ghost" onClick={() => dismissDialog(() => settle(false))} data-autofocus={request.danger || undefined}>
              취소
            </Button>
            <Button variant={request.danger ? 'danger' : 'primary'} onClick={() => settle(true)} data-autofocus={!request.danger || undefined}>
              {request.ok ?? '확인'}
            </Button>
          </div>
        </>
      ) : null}
    </Modal>
  );
}

// Provider

/** Toasts, popover and confirm dialog for one window. Wrap each React root once. */
export function UiProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextToast = useRef(0);
  const dismiss = useCallback((id: number) => {
    setToasts((items) => items.map((item) => (item.id === id ? { ...item, leaving: true } : item)));
    window.setTimeout(() => setToasts((items) => items.filter((item) => item.id !== id)), 300);
  }, []);
  const toast = useCallback<ToastFn>(
    (message, options = {}) => {
      const id = ++nextToast.current;
      setToasts((items) => [...items.slice(-3), { id, message, leaving: false, ...options }]);
      // A timer would take the action away before a keyboard or screen reader user gets to it (WCAG 2.2.1).
      if (!options.action) window.setTimeout(() => dismiss(id), options.ms ?? 3600);
    },
    [dismiss],
  );

  const [popover, setPopover] = useState<PopoverState | null>(null);
  const closePopover = useCallback(() => setPopover(null), []);
  const popoverApi = useMemo(() => ({ open: (state: PopoverState) => setPopover(state), close: closePopover }), [closePopover]);

  const [confirmRequest, setConfirmRequest] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);
  const confirm = useCallback<ConfirmFn>((options) => {
    resolver.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setConfirmRequest({ ...options });
    });
  }, []);
  const settle = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setConfirmRequest(null);
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      <PopoverContext.Provider value={popoverApi}>
        <ConfirmContext.Provider value={confirm}>
          {children}
          <PopoverHost state={popover} close={closePopover} />
          <ConfirmHost request={confirmRequest} settle={settle} />
          <ToastHost items={toasts} dismiss={dismiss} />
          <TooltipHost />
        </ConfirmContext.Provider>
      </PopoverContext.Provider>
    </ToastContext.Provider>
  );
}
